import theme from '@/constants/theme';
import { KeyboardArrowDown } from '@mui/icons-material';
import FormLabel from '@/components/input/FormLabel';
import { useEffect, useRef, useState } from 'react';
import Benefits from './Benefits';
import Textarea from '@/components/input/Textarea';
import Input from '@/components/input/Input';

export interface OverView {
  importance: string;
  benefits: string[];
  emoji: string | null;
}

export const OVERVIEW_DEFAULT: OverView = {
  importance: '',
  benefits: [],
  emoji: null,
};

interface Props {
  overviewData: string | null;
  setOverviewData: React.Dispatch<React.SetStateAction<string | null>>;
}

const OverviewEditor: React.FC<Props> = ({ overviewData, setOverviewData }) => {
  const [overview, setOverview] = useState<OverView>(OVERVIEW_DEFAULT);

  const [expand, setExpand] = useState(false);
  const [height, setHeight] = useState(0);

  const coverFieldsContainer = useRef<HTMLDivElement>(null);

  const handleExpandStage = () => {
    setExpand((prev) => !prev);
  };

  // hidrate overview data at first render
  useEffect(() => {
    if (!overviewData) {
      setOverview(OVERVIEW_DEFAULT);
      return;
    }
    setOverview(JSON.parse(overviewData));
  }, [overviewData]);

  // handle safe quiz data with debounce
  useEffect(() => {
    if (!overview) return;
    const timerId = setTimeout(() => {
      setOverviewData(JSON.stringify(overview));
    }, 350);
    return () => clearTimeout(timerId);
  }, [overview, setOverviewData]);

  useEffect(() => {
    if (expand) {
      const currentHeight = coverFieldsContainer.current?.scrollHeight ?? 0;
      setHeight(currentHeight);
    } else {
      setHeight(0);
    }
  }, [expand, overview]);

  return (
    <fieldset style={{ display: 'flex', flexDirection: 'column', gap: 12, border: 'none' }}>
      <FormLabel
        label="Overview del contenido"
        helperText="Esta información se mostrará a los usuarios antes y después de comenzar el contenido."
      />
      <div
        style={{
          border: `1px solid ${theme.colors.border}`,

          display: 'flex',
          flexDirection: 'column',
          borderRadius: 8,
        }}
      >
        <button
          type="button"
          style={{
            color: theme.colors.primary,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            border: 'none',
            backgroundColor: 'inherit',
            fontSize: 16,
            fontFamily: 'inherit',
            padding: 12,
            cursor: 'pointer',
          }}
          onClick={handleExpandStage}
        >
          <KeyboardArrowDown
            style={{
              transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 300ms ease-out',
            }}
          />
          <span>{expand ? 'Ocultar' : 'Mostrar'} detalles del overview</span>
        </button>
        <div
          aria-hidden={!expand}
          style={{
            overflow: 'hidden',
            height: height,
            transition: 'height 300ms ease-out',
            visibility: expand ? 'visible' : 'hidden',
          }}
        >
          <div
            ref={coverFieldsContainer}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              minHeight: 'fit-content',
              opacity: expand ? 1 : 0,
              transition: `opacity 200ms ease-out ${expand ? '0.2s' : '0s'}`,
              padding: 12,
            }}
          >
            <Input
              label="Emoji de pantalla de completado"
              helperText="En caso de no tener un emoji se rendizará un icono por defecto."
              value={overview?.emoji ?? ''}
              onChange={(e) =>
                setOverview((prev) => ({
                  ...prev,
                  emoji: e.target.value,
                }))
              }
            />
            <Textarea
              label="Importancia"
              helperText="Esta información se mostrará a los usuarios antes de comenzar el contenido."
              sx={{ width: '100%' }}
              value={overview?.importance ?? ''}
              onChange={(e) =>
                setOverview((prev) => ({
                  ...prev,
                  importance: e.target.value,
                }))
              }
            />
            <Benefits benefits={overview?.benefits ?? []} setOverview={setOverview} />
          </div>
        </div>
      </div>
    </fieldset>
  );
};

export default OverviewEditor;
